import React from 'react';
import { useGetOne, useRedirect, useTranslate } from 'react-admin';
import { Check, Payment, Refresh, Edit } from '@material-ui/icons';
import { Box, Button, Chip } from '@material-ui/core';
import DownloadIcon from '@material-ui/icons/GetApp';
import { useParams } from 'react-router-dom';
import resourceSlug from '../../constant/resource-slug';
import { useInvoiceDetailsNavigation, useInvoiceStatus, useRegenerateInvoice, useValidateInvoice } from './hooks';
import ExportButton from './components/ExportButton';
import GeneralInfo from './components/InvoiceDetails/GeneralInfo';
import ToolbarMenu from '../../base/components/action-menu/toolbar-menu';
import PaymentStatus from '../payment/components/PaymentStatus';
import InvoiceStatus from './components/InvoiceStatus';
import MakePaymentButton from './components/MakePaymentButton';
import DetailsSkeleton from '../../base/components/common/DetailsSkeleton';
import ExchangeRatesTable from './components/InvoiceDetails/ExchangeRatesTable';
import PaymentTable from './components/InvoiceDetails/PaymentTable';
import ChargeDetailsTable from './components/InvoiceDetails/ChargeDetailsTable';
import { INVOICE_SERVICE } from '../../constant/invoice';
import UpdateAdjustmentModal from './components/UpdateAdjustmentModal';

const InvoiceShow = () => {
  const t = useTranslate();
  const { id } = useParams();
  const { data, loading, error, refetch, loaded } = useGetOne(resourceSlug.INVOICE, id);
  const redirect = useRedirect();

  const { isRegenerating, regenerateInvoice } = useRegenerateInvoice({
    onSuccess: refetch,
  });
  const { validateInvoice, isValidating } = useValidateInvoice({
    onSuccess: refetch,
  });

  const {
    isExportableInvoice,
    isPayableInvoice,
    isRegeneratableInvoice,
    isValidatableInvoice,
    isAdjusmentEditableInvoice,
  } = useInvoiceStatus();

  useInvoiceDetailsNavigation();

  if (loading && !loaded) {
    return <DetailsSkeleton />;
  }

  if (error) {
    redirect(`/${resourceSlug.INVOICE}`);
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          gap: 4,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-end',
            sm: 'center',
          }}
        >
          <Box
            display="flex"
            flexDirection={{
              xs: 'column',
              sm: 'row',
            }}
            sx={{
              gap: 4,
            }}
          >
            <Chip color="default" label={data?.invoice?.invoiceNo} />
            <PaymentStatus status={data?.invoice?.paymentStatus} />
            <InvoiceStatus status={data?.invoice?.invoiceStatus} />
          </Box>
          <Box display="flex">
            {isPayableInvoice(data?.invoice) && (
              <MakePaymentButton
                invoiceIds={[id]}
                onSuccess={() => refetch()}
                trigger={
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    style={{
                      lineHeight: 1,
                    }}
                    startIcon={<Payment fontSize="small" />}
                  >
                    {t('resources.invoice.makePayment')}
                  </Button>
                }
              />
            )}
            {isValidatableInvoice(data?.invoice) && (
              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<Check fontSize="small" />}
                style={{
                  lineHeight: 1,
                }}
                onClick={() => validateInvoice(id)}
                disabled={isValidating}
              >
                {t('resources.invoice.validate')}
              </Button>
            )}
            {isAdjusmentEditableInvoice(data?.invoice) && (
              <UpdateAdjustmentModal
                invoiceNo={data?.invoice?.invoiceNo}
                invoiceId={data?.invoice?.id}
                amount={data?.invoice?.adjustment?.amount}
                description={data?.invoice?.adjustment?.description}
                currencyCode={data?.invoice?.currencyCode}
                totalInvoiceAmount={data?.invoice?.invoiceAmount}
                trigger={
                  <Button
                    size="small"
                    color="primary"
                    variant="text"
                    startIcon={<Edit fontSize="small" />}
                    style={{
                      lineHeight: 1,
                    }}
                  >
                    {t('resources.invoice.adjustInvoice')}
                  </Button>
                }
                onSuccess={() => {
                  refetch();
                }}
              />
            )}
            <ToolbarMenu
              basePath="/invoice"
              record={data}
              resource={resourceSlug.INVOICE}
              hasList
              hasEdit={false}
              extraOptions={[
                {
                  condition: isRegeneratableInvoice(data?.invoice),
                  name: t('resources.invoice.regenerate'),
                  icon: (
                    <Refresh
                      style={{
                        width: 20,
                        height: 20,
                      }}
                    />
                  ),
                  hideLabel: true,
                  onClick: () => regenerateInvoice(id),
                  disabled: isRegenerating,
                },
              ]}
            />
            {isExportableInvoice() && (
              <ExportButton
                invoiceId={id}
                trigger={
                  <Button variant="text" size="small" color="primary">
                    <DownloadIcon fontSize="small" />
                  </Button>
                }
              />
            )}
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            gap: 12,
          }}
        >
          <GeneralInfo record={data} />
          <ChargeDetailsTable
            record={data}
            fieldKey="ggrCharge"
            title={t(`resources.invoice.serviceDetails.${INVOICE_SERVICE.MONTHLY_GGR}`)}
          />
          <ChargeDetailsTable
            record={data}
            fieldKey="mktFreeSpin"
            title={t(`resources.invoice.serviceDetails.${INVOICE_SERVICE.MKT_FREE_SPIN}`)}
          />
          <ChargeDetailsTable
            record={data}
            fieldKey="dailyProgram"
            title={t(`resources.invoice.serviceDetails.${INVOICE_SERVICE.DAILY_PROGRAM}`)}
          />
          <PaymentTable record={data} />
          <ExchangeRatesTable record={data} />
        </Box>
      </Box>
    </>
  );
};

export default InvoiceShow;
